import React, { useContext, useEffect, useRef, useState } from "react";
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from "react-icons/ai";
import { FiLogOut } from "react-icons/fi";
import { DContext } from "../../context/Datacontext";

/**
 * Logout page
 * - Calls backend /logout on mount (credentials included)
 * - Displays loading UI while request is in-flight
 * - Shows success or error states and redirects to "/" on success
 *
 * Theme: uses your primary-300 (#3026dc) and secondary-300 (#9914dc) as accents.
 */
export default function Logout() {
    const { BeURL, setIsAuth, setCurrentUser, showAlert, handleLogout } = useContext(DContext);

    // status: 'idle' | 'loading' | 'success' | 'error'
    const [status, setStatus] = useState("loading");
    const [message, setMessage] = useState("Logging you out...");
    const [errorDetail, setErrorDetail] = useState("");


    useEffect(() => {
        setTimeout(() => {
            handleLogout();
        }, 2000)
    }, []);

    // UX pieces
    const accentPrimaryText = "text-[color:var(--primary-300,#3026dc)]";

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
            <div className="w-full max-w-2xl">
                <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-6 sm:p-10">
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                        {/* Icon circle */}
                        <div
                            className={`flex items-center justify-center rounded-full w-20 h-20 flex-shrink-0 ${accentPrimaryText} bg-gradient-to-tr from-[rgba(48,38,220,0.08)] to-[rgba(153,20,220,0.03)]`}
                            aria-hidden
                        >
                            <FiLogOut className="w-10 h-10" />
                        </div>

                        <div className="flex-1">
                            <h1 className="text-lg sm:text-2xl font-semibold text-gray-800">Logging out</h1>
                            <p className="mt-2 text-sm text-gray-500">
                                We are signing you out of your account. This may take a moment.
                            </p>

                            {/* status card */}
                            <div className="mt-4 p-4 rounded-xl border border-gray-100 bg-gradient-to-b from-white to-gray-50">
                                {/* Loading */}
                                {status === "loading" && (
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                        <div className="flex items-center gap-4">
                                            {/* spinner */}
                                            <div
                                                className="w-12 h-12 rounded-full border-4 border-t-[4px] border-gray-200 inline-block animate-spin"
                                                style={{ borderTopColor: "#3026dc" }}
                                                aria-hidden
                                            />
                                            <div>
                                                <div className="text-base font-medium text-gray-800">{message}</div>
                                                <div className="mt-1 text-xs text-gray-500">Closing your session securely…</div>
                                            </div>
                                        </div>

                                        {/* indeterminate bar */}
                                        <div className="w-full mt-3 sm:mt-0 sm:ml-6">
                                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                <div
                                                    className="h-2 rounded-full"
                                                    style={{
                                                        width: "40%",
                                                        background: "linear-gradient(90deg,#3026dc,#9914dc)",
                                                        animation: "move 1.6s linear infinite",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* success */}
                                {status === "success" && (
                                    <div className="flex items-center gap-4">
                                        <AiOutlineCheckCircle className="w-10 h-10 text-[color:var(--primary-300,#3026dc)]" />
                                        <div>
                                            <div className="text-base font-medium text-gray-800">Logged out</div>
                                            <div className="mt-1 text-sm text-gray-500">You have been logged out successfully. Redirecting…</div>
                                        </div>
                                    </div>
                                )}

                                {/* error */}
                                {status === "error" && (
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                        <div className="flex items-center gap-4">
                                            <AiOutlineCloseCircle className="w-10 h-10 text-red-500" />
                                            <div>
                                                <div className="text-base font-medium text-gray-800">Could not login you out</div>
                                                <div className="mt-1 text-sm text-red-600">{errorDetail}</div>
                                            </div>
                                        </div>

                                        <div className="mt-4 sm:mt-0 sm:ml-auto flex gap-2">
                                            <button
                                                onClick={handleLogout}
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[color:var(--primary-300,#3026dc)] text-white text-sm hover:opacity-95 transition"
                                            >
                                                Retry
                                            </button>

                                            <button
                                                onClick={() => {
                                                    // reset local auth & navigate home as fallback
                                                    try {
                                                        setIsAuth(false);
                                                        setCurrentUser({});
                                                    } catch (e) { }
                                                    window.location.href = "/";
                                                }}
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition"
                                            >
                                                Go Home
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* footer / small note */}
                            <div className="mt-4 flex items-center justify-between gap-4">
                                <p className="text-xs text-gray-400">
                                    If this takes longer than usual, please check your connection and try again.
                                </p>

                                {status === "loading" && (
                                    <div className="text-xs text-gray-500">Secure logout in progress…</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* extra small footer */}
                    <div className="mt-6 text-center text-xs text-gray-400">
                        © {new Date().getFullYear()} SAN Engineering solutions. All rights reserved.
                    </div>
                </div>
            </div>

            {/* simple keyframes style injection for indeterminate bar animation */}
            <style>{`
        @keyframes move {
          0% { transform: translateX(-0%); }
          50% { transform: translateX(40%); }
          100% { transform: translateX(120%); }
        }
      `}</style>
        </div>
    );
}
